import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Mail, 
  ArrowRight,
  RefreshCw,
  Loader2
} from "lucide-react";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const API_URL = process.env.REACT_APP_API_URL;

export default function ApplicationStatus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("id");
  const email = searchParams.get("email");
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkStatus = async () => {
    setLoading(true);
    try {
      let response;
      if (applicationId) {
        response = await axios.get(`${API_URL}/api/applications/status/${applicationId}`);
        setStatus(response.data);
      } else if (email) {
        response = await axios.get(`${API_URL}/api/applications/check-email/${encodeURIComponent(email)}`);
        if (response.data.has_application) {
          setStatus({
            status: response.data.status,
            applied_at: response.data.applied_at
          });
        } else {
          setStatus(null);
        }
      }
      setError(null);
    } catch (err) {
      console.error("Failed to check status:", err);
      setError("Could not retrieve application status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (applicationId || email) {
      checkStatus();
    } else {
      setLoading(false);
    }
  }, [applicationId, email]);

  const statusConfig = {
    pending: {
      icon: Clock,
      color: "text-amber-400",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      title: "Application Under Review",
      description: "Your application is being reviewed by our team. This typically takes 24-48 hours."
    },
    approved: {
      icon: CheckCircle2,
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      title: "Application Approved!",
      description: "Congratulations! Your application has been approved. You can now log in to your account."
    },
    rejected: {
      icon: XCircle,
      color: "text-red-400",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30",
      title: "Application Not Approved",
      description: "Unfortunately, your application was not approved at this time. Please contact us for more information."
    }
  };

  const currentStatus = status?.status ? statusConfig[status.status] : null;
  const StatusIcon = currentStatus?.icon || Clock;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={SPEAKR_LOGO} alt="Speakr" className="h-12 mx-auto" />
          </Link>
        </div>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="text-center">
            <CardTitle className="text-white">Application Status</CardTitle>
            <CardDescription className="text-slate-400">
              Track the status of your Speakr application
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-10 h-10 text-blue-400 animate-spin mb-4" />
                <p className="text-slate-400">Checking status...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 mb-4">{error}</p>
                <Button onClick={checkStatus} variant="outline" className="border-slate-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : !status ? (
              <div className="text-center py-8">
                <p className="text-slate-400 mb-6">No application found with the provided information.</p>
                <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-blue-600 to-purple-600">
                  Submit an Application
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Status Badge */}
                <div className={`flex flex-col items-center p-6 rounded-xl ${currentStatus?.bgColor} border ${currentStatus?.borderColor}`}>
                  <StatusIcon className={`w-16 h-16 ${currentStatus?.color} mb-4`} />
                  <Badge className={`${currentStatus?.bgColor} ${currentStatus?.color} ${currentStatus?.borderColor} text-sm px-4 py-1`}>
                    {status.status?.toUpperCase()}
                  </Badge>
                  <h3 className="text-xl font-semibold text-white mt-4 text-center">
                    {currentStatus?.title}
                  </h3>
                  <p className="text-slate-400 text-center mt-2 text-sm">
                    {currentStatus?.description}
                  </p>
                </div>

                {/* Application Info */}
                {status.business_name && (
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Business Name</p>
                    <p className="text-white font-medium">{status.business_name}</p>
                  </div>
                )}

                {status.applied_at && (
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Applied On</p>
                    <p className="text-white font-medium">
                      {new Date(status.applied_at).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </p>
                  </div>
                )}

                {/* Actions based on status */}
                <div className="space-y-3 pt-4">
                  {status.status === "pending" && (
                    <>
                      <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-400" />
                        <p className="text-sm text-blue-300">
                          You'll receive an email once your application is reviewed.
                        </p>
                      </div>
                      <Button 
                        onClick={checkStatus}
                        variant="outline" 
                        className="w-full border-slate-700 text-slate-300"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Status
                      </Button>
                    </>
                  )}

                  {status.status === "approved" && (
                    <Button 
                      onClick={() => navigate("/login")}
                      className="w-full bg-gradient-to-r from-emerald-600 to-blue-600"
                    >
                      Log In to Your Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}

                  {status.status === "rejected" && (
                    <Button 
                      onClick={() => navigate("/contact")}
                      variant="outline"
                      className="w-full border-slate-700 text-slate-300"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 mt-6 text-sm">
          <Link to="/" className="text-slate-500 hover:text-white transition-colors">
            Home
          </Link>
          <Link to="/contact" className="text-slate-500 hover:text-white transition-colors">
            Contact Us
          </Link>
          <Link to="/login" className="text-slate-500 hover:text-white transition-colors">
            Login
          </Link>
        </div>
      </div>
    </div>
  );
}
